import { DeclineIcon } from '@synergy/theme';
import { Field, FieldArray, FieldProps, useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';
import { Grid, IconButton } from '@mui/material';
import { PoolButton, PoolTextField, PoolTypography } from '@synergy/components';
import { getSuffixCurrency } from '@synergy/utils';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { SupplierConditionsFormValues } from '../models/supplier-conditions-form-values';
import { bestLocaleSelector } from '../../../../../../common/selectors/locale-selectors';
import { useAppSelector } from '../../../../../../store/use-appstate-selector';

export const SupplierDetailConditionsDeliveryTerms = () => {
    const locale = useAppSelector(bestLocaleSelector);
    const currency = useAppSelector(state => state.settings.currency);
    const { values } = useFormikContext<SupplierConditionsFormValues>();

    return (
        <div>
            <Grid item={true} xs={12}>
                <PoolTypography styleType="sectionTitle" id="app.contactsAndOrganizations.detail.deliveryTerms" />
            </Grid>

            <Grid container={true} spacing={2}>
                <Grid container={true} item={true} spacing={2}>
                    <Grid item={true} xs={4}>
                        <Field name="shippingCost">
                            {({ field, form, meta }: FieldProps<number | null>) => (
                                <PoolTextField
                                    {...field}
                                    label={<FormattedMessage id="app.contactsAndOrganizations.detail.deliveryCosts" />}
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                    fullWidth={true}
                                    isNumeric={true}
                                    fixedDecimalScale={true}
                                    decimalScale={2}
                                    noNegativeNumbers={true}
                                    onChange={e => form.setFieldValue(field.name, e.target.value ?? null)}
                                    suffix={getSuffixCurrency(field.value, currency, locale)}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4}>
                        <Field name="freeShippingThreshold">
                            {({ field, meta }: FieldProps<number>) => (
                                <PoolTextField
                                    {...field}
                                    label={
                                        <FormattedMessage id="app.contactsAndOrganizations.detail.freeShippingThreshold" />
                                    }
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                    fullWidth={true}
                                    isNumeric={true}
                                    fixedDecimalScale={true}
                                    decimalScale={2}
                                    noNegativeNumbers={true}
                                    suffix={getSuffixCurrency(field.value, currency, locale)}
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
                {/* Shipping that steps up with the order value instead of one
                    flat amount (key-user brief A3.3). Below the first step the
                    shipping cost above applies, the free-shipping threshold
                    still wins. */}
                <FieldArray name="shippingCostTiers">
                    {helper => (
                        <Grid container={true} item={true} spacing={2}>
                            <Grid item={true} xs={12}>
                                <PoolTypography
                                    typography="caption"
                                    id="app.contactsAndOrganizations.detail.shippingCostTiersHint"
                                />
                            </Grid>
                            {(values.shippingCostTiers ?? []).map((_, index) => (
                                <Grid container={true} item={true} spacing={2} key={index} alignItems="center">
                                    <Grid item={true} xs={4}>
                                        <Field name={`shippingCostTiers.${index}.fromNetValue`}>
                                            {({ field, form }: FieldProps<number | null>) => (
                                                <PoolTextField
                                                    {...field}
                                                    label={
                                                        <FormattedMessage id="app.contactsAndOrganizations.detail.shippingCostTierFrom" />
                                                    }
                                                    fullWidth={true}
                                                    isNumeric={true}
                                                    fixedDecimalScale={true}
                                                    decimalScale={2}
                                                    noNegativeNumbers={true}
                                                    onChange={e =>
                                                        form.setFieldValue(field.name, e.target.value ?? null)
                                                    }
                                                    suffix={getSuffixCurrency(field.value, currency, locale)}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item={true} xs={4}>
                                        <Field name={`shippingCostTiers.${index}.shippingCost`}>
                                            {({ field, form }: FieldProps<number | null>) => (
                                                <PoolTextField
                                                    {...field}
                                                    label={
                                                        <FormattedMessage id="app.contactsAndOrganizations.detail.deliveryCosts" />
                                                    }
                                                    fullWidth={true}
                                                    isNumeric={true}
                                                    fixedDecimalScale={true}
                                                    decimalScale={2}
                                                    noNegativeNumbers={true}
                                                    onChange={e =>
                                                        form.setFieldValue(field.name, e.target.value ?? null)
                                                    }
                                                    suffix={getSuffixCurrency(field.value, currency, locale)}
                                                />
                                            )}
                                        </Field>
                                    </Grid>
                                    <Grid item={true} xs={1}>
                                        <IconButton onClick={() => helper.remove(index)}>
                                            <DeclineIcon />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))}
                            <Grid item={true} xs={12}>
                                <PoolButton
                                    styleType="thirdLevel"
                                    startIcon={<AddIcon color="primary" />}
                                    onClick={() => helper.push({ fromNetValue: '', shippingCost: '' })}
                                >
                                    <FormattedMessage id="app.contactsAndOrganizations.detail.addShippingCostTier" />
                                </PoolButton>
                            </Grid>
                        </Grid>
                    )}
                </FieldArray>
                <Grid container={true} item={true} spacing={2}>
                    <Grid item={true} xs={4}>
                        <Field name="minimumOrderValue">
                            {({ field, meta }: FieldProps<number | null>) => (
                                <PoolTextField
                                    {...field}
                                    label={
                                        <FormattedMessage id="app.contactsAndOrganizations.detail.minimumOrderValue" />
                                    }
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                    fullWidth={true}
                                    isNumeric={true}
                                    fixedDecimalScale={true}
                                    decimalScale={2}
                                    noNegativeNumbers={true}
                                    suffix={getSuffixCurrency(field.value, currency, locale)}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={4}>
                        <Field name="minimumQuantitySurcharge">
                            {({ field, meta }: FieldProps<number>) => (
                                <PoolTextField
                                    {...field}
                                    label={
                                        <FormattedMessage id="app.contactsAndOrganizations.detail.minimumQuantitySurcharge" />
                                    }
                                    error={meta.touched && !!meta.error}
                                    helperText={
                                        meta.touched && meta.error ? <FormattedMessage id={meta.error} /> : null
                                    }
                                    fullWidth={true}
                                    isNumeric={true}
                                    fixedDecimalScale={true}
                                    decimalScale={2}
                                    noNegativeNumbers={true}
                                    suffix={getSuffixCurrency(field.value, currency, locale)}
                                />
                            )}
                        </Field>
                    </Grid>
                </Grid>
            </Grid>
        </div>
    );
};
